import { AudienceCategoryItem, AudiencePlatformItem, CountryItem, AudienceItem } from '@digiseg-labs/api-client';
import { Card, Flex, Heading, Button } from '@radix-ui/themes';
import { ReactNode, forwardRef, useEffect, useMemo, useState } from 'react';
import AudienceListItem from './AudienceListItem';

const AUDIENCES_PER_CATEGORY = 5;

type Props = {
  category: AudienceCategoryItem;
  countries: CountryItem[];
  platforms: AudiencePlatformItem[];
  selectedCountry: CountryItem | undefined;
  selectedPlatform: AudiencePlatformItem | undefined;
  onCountrySelected: (country: CountryItem | undefined) => void;
  onPlatformSelected: (platform: AudiencePlatformItem | undefined) => void;
  onCategoryFocus?: (categoryCode: string) => void;
  isFocused?: boolean;
  disableAudienceLimit?: boolean;
  shareControls?: ReactNode;
  buildShareUrl: (options: { categoryCode?: string; audienceCode?: string; includeCountry?: boolean; includePlatform?: boolean }) => string;
  openAudienceCode?: string | null;
  onModalOpenChange?: (audienceCode: string, open: boolean) => void;
};

const AudienceCategorySection = forwardRef<HTMLDivElement, Props>(({
  category,
  countries,
  platforms,
  selectedCountry,
  selectedPlatform,
  onCountrySelected,
  onPlatformSelected,
  onCategoryFocus,
  isFocused = false,
  disableAudienceLimit = false,
  shareControls,
  buildShareUrl,
  openAudienceCode,
  onModalOpenChange,
}: Props, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const limitAudiences = !disableAudienceLimit;
  const audiences = category.audiences ?? [];
  const hiddenCount = limitAudiences ? Math.max(audiences.length - AUDIENCES_PER_CATEGORY, 0) : 0;

  useEffect(() => {
    setIsExpanded(false);
  }, [category.code, hiddenCount, limitAudiences]);

  const visibleAudiences = useMemo(() => {
    if (!limitAudiences) return audiences;
    if (isExpanded) return audiences;
    return audiences.slice(0, AUDIENCES_PER_CATEGORY);
  }, [audiences, isExpanded, limitAudiences]);

  const handleCategoryActivate = () => {
    if (onCategoryFocus) {
      onCategoryFocus(category.code);
    }
  };
  const cardClasses = ['audience-category-card', isFocused ? 'is-focused' : null].filter(Boolean).join(' ');
  const headingClasses = onCategoryFocus ? 'category-heading' : undefined;

  return (
    <Card
      ref={ref}
      data-category-code={category.code}
      variant="surface"
      className={cardClasses}
    >
      <Flex align="center" justify="between" gap="3" mb="3">
        <Heading
          size="5"
          onClick={handleCategoryActivate}
          onKeyDown={(event) => {
            if ((event.key === 'Enter' || event.key === ' ') && onCategoryFocus) {
              event.preventDefault();
              handleCategoryActivate();
            }
          }}
          role={onCategoryFocus ? 'button' : undefined}
          tabIndex={onCategoryFocus ? 0 : undefined}
          className={headingClasses}
        >
          {category.display_name}
        </Heading>
        {shareControls}
      </Flex>
      <Flex direction="column" gap="2">
        {visibleAudiences.length > 0 ? (
          visibleAudiences.map((audience: AudienceItem) => (
            <AudienceListItem
              key={audience.code}
              categoryName={category.display_name}
              categoryCode={category.code}
              audience={audience}
              countries={countries}
              platforms={platforms}
              selectedCountry={selectedCountry}
              selectedPlatform={selectedPlatform}
              onCountrySelected={onCountrySelected}
              onPlatformSelected={onPlatformSelected}
              onCategorySelected={onCategoryFocus}
              buildShareUrl={buildShareUrl}
              isModalOpen={openAudienceCode === audience.code}
              onModalOpenChange={onModalOpenChange}
            />
          ))
        ) : (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#888' }}>No audiences available.</div>
        )}
      </Flex>
      {limitAudiences && hiddenCount > 0 && (
        <Flex justify="center" mt="2">
          <Button
            className='copy-audience-id-btn'
            variant="ghost"
            color="gray"
            size="1"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? 'Show less' : `Show all ${audiences.length}`}
          </Button>
        </Flex>
      )}
    </Card>
  );
});

AudienceCategorySection.displayName = 'AudienceCategorySection';

export default AudienceCategorySection;
